/**
 * Common phone-call utilities.
 *
 * Phone numbers across the site are stored inconsistently — some start with a
 * leading 0, some carry a `+91-` prefix, some are toll-free/landline, some are
 * bare 10-digit mobiles. These helpers accept ANY of those formats, sanitize
 * them into a dialer-safe value, and initiate the call.
 *
 * NOTE: This only affects the value passed to the dialer. Numbers DISPLAYED in
 * the UI must keep using their original variable — do not swap display text.
 */

// Toll-free / universal-access ranges: dial as-is, never prepend +91.
const SPECIAL_PREFIXES = ["1800", "1860", "1900"];

// Emergency / service short codes (e.g. 100, 108, 112, 1091, 14416).
const SHORT_CODE_MAX_LEN = 5;

const COUNTRY_CODE = "91";

/**
 * Convert any raw phone number into a dialer-safe string (without the `tel:`
 * scheme). Returns `null` when the input is empty/unusable.
 *
 * @param {string|number} raw
 * @returns {string|null}
 */
export function sanitizePhoneForCall(raw) {
    if (raw === null || raw === undefined) return null;

    const str = String(raw).trim();
    if (!str) return null;

    const hadPlus = str.startsWith("+");
    const digits = str.replace(/\D/g, "");
    if (!digits) return null;

    // 1) Already international: `+…` or `91XXXXXXXXXX` (12 digits) → normalize.
    if (hadPlus) {
        return `+${digits}`;
    }
    if (digits.length === 12 && digits.startsWith(COUNTRY_CODE)) {
        return `+${digits}`;
    }

    // 2) Toll-free / universal-access → dial as-is, no country code.
    if (SPECIAL_PREFIXES.some((prefix) => digits.startsWith(prefix))) {
        return digits;
    }

    // 3) Short codes → dial as-is, no country code.
    if (digits.length <= SHORT_CODE_MAX_LEN) {
        return digits;
    }

    // 4) Mobile OR landline (unified): drop a single leading trunk 0. Indian
    //    STD code + subscriber number = 10 digits after the 0, same as a
    //    mobile, so both take this path.
    const withoutTrunk = digits.startsWith("0") ? digits.slice(1) : digits;
    if (withoutTrunk.length === 10) {
        return `+${COUNTRY_CODE}${withoutTrunk}`;
    }

    // 5) Fallback: bare local number without STD code (can't infer city) or an
    //    unexpected length → dial the digits as-is rather than guess a prefix.
    if (typeof console !== "undefined") {
        console.warn(`[callUtils] Unrecognized phone format, dialing as-is: ${str}`);
    }
    return withoutTrunk;
}

/**
 * Build a `tel:` href from any raw number. Use for anchor / next `<Link>`
 * elements that keep semantic links. Returns `undefined` when unusable.
 *
 * @param {string|number} raw
 * @returns {string|undefined}
 */
export function callHref(raw) {
    const clean = sanitizePhoneForCall(raw);
    return clean ? `tel:${clean}` : undefined;
}

/**
 * Sanitize and initiate a phone call. Use for onClick / imperative handlers.
 * No-ops safely when the number is unusable.
 *
 * @param {string|number} raw
 */
export function initiateCall(raw) {
    const clean = sanitizePhoneForCall(raw);
    if (!clean) return;
    if (typeof window !== "undefined") {
        window.location.href = `tel:${clean}`;
    }
}
